import { Link } from "@inertiajs/react";
import { ChevronDown, Check } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/DropdownMenu";

export interface ScopeNode {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  depth: number;
  is_leaf: boolean;
  authorized: boolean;
}

interface Props {
  scopeTree: ScopeNode[];
  currentNodeId: string;
  currentNodeName: string;
  hrefForChannel: (slug: string) => string;
  requireLeaf?: boolean;
  /** Heading at the top of the menu. */
  label?: string;
  /** Replaces the default button, for toolbars with their own button look. */
  trigger?: React.ReactElement;
}

function buildChildrenIndex(tree: ScopeNode[]): Map<string | null, ScopeNode[]> {
  const map = new Map<string | null, ScopeNode[]>();
  tree.forEach((node) => {
    const list = map.get(node.parent_id) ?? [];
    list.push(node);
    map.set(node.parent_id, list);
  });
  return map;
}

function NodeRow({
  node,
  childrenIndex,
  currentNodeId,
  hrefForChannel,
  depth,
  requireLeaf,
}: {
  node: ScopeNode;
  childrenIndex: Map<string | null, ScopeNode[]>;
  currentNodeId: string;
  hrefForChannel: (slug: string) => string;
  depth: number;
  requireLeaf: boolean;
}) {
  const isActive = node.id === currentNodeId;
  const children = childrenIndex.get(node.id) ?? [];
  const clickable = node.authorized && (requireLeaf ? node.is_leaf : true);

  return (
    <div>
      {clickable ? (
        <Link
          href={hrefForChannel(node.slug)}
          className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-xs no-underline outline-none hover:bg-accent"
          style={{
            paddingLeft: 8 + depth * 12,
            color: isActive ? "var(--primary)" : "var(--foreground)",
            fontWeight: isActive ? 600 : 400,
          }}
        >
          <span>{node.name}</span>
          {isActive && <Check className="h-3.5 w-3.5 text-primary" />}
        </Link>
      ) : (
        <div
          className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs"
          style={{
            paddingLeft: 8 + depth * 12,
            color: "var(--muted-foreground)",
            cursor: "default",
          }}
          title={node.authorized ? "Select a leaf channel" : "No access"}
        >
          <span>{node.name}</span>
        </div>
      )}
      {children.map((child) => (
        <NodeRow
          key={child.id}
          node={child}
          childrenIndex={childrenIndex}
          currentNodeId={currentNodeId}
          hrefForChannel={hrefForChannel}
          depth={depth + 1}
          requireLeaf={requireLeaf}
        />
      ))}
    </div>
  );
}

export default function ChannelMenu({ scopeTree, currentNodeId, currentNodeName, hrefForChannel, requireLeaf = true, label = "Scope", trigger }: Props) {
  const childrenIndex = buildChildrenIndex(scopeTree);
  const ids = new Set(scopeTree.map((n) => n.id));
  const roots = scopeTree.filter((n) => n.parent_id === null || !ids.has(n.parent_id));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? <Button
          variant="outline"
          size="sm"
          className="h-auto gap-2 px-3 py-1.5 text-left focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <span className="text-xs font-semibold text-foreground">{currentNodeName}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[16rem] max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {roots.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No channels</div>
        ) : (
          roots.map((node) => (
            <NodeRow
              key={node.id}
              node={node}
              childrenIndex={childrenIndex}
              currentNodeId={currentNodeId}
              hrefForChannel={hrefForChannel}
              depth={0}
              requireLeaf={requireLeaf}
            />
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
